import jsPDF from 'jspdf';
import horeImg from '../obr/hore.png';
import doleImg from '../obr/dole.png';
import { nacitajPdfFonty } from './pdfFonty';

const OKRAJ_X = 15;
const SIRKA_STRANY = 210;
const VYSKA_STRANY = 297;
const SIRKA_OBSAHU = SIRKA_STRANY - OKRAJ_X * 2;

const VYSKA_HLAVY = 30;
const VYSKA_PATY = 20;

const FIXNA_VYSKA_FINCLOUD_TIP = 50;

const START_Y = 42;
const MAX_Y = 266;

const FARBA_NADPIS = [120, 102, 67];
const FARBA_TEXT = [45, 45, 45];
const FARBA_JEMNA = [110, 110, 110];
const FARBA_LINKA = [205, 196, 177];
const FARBA_PATA = [255, 255, 255];

function bezpecnyNazovSuboru(nazov) {
  const cisty = String(nazov || 'Ponuka_hypoteka')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_');

  return cisty.toLowerCase().endsWith('.pdf') ? cisty : `${cisty}.pdf`;
}

function hodnotaText(hodnota, fallback = '—') {
  if (hodnota === null || hodnota === undefined || hodnota === '') return fallback;
  return String(hodnota);
}

function cisloNaText(hodnota, desatinne = 2) {
  const cislo = Number(hodnota);
  if (!Number.isFinite(cislo)) return '—';

  return new Intl.NumberFormat('sk-SK', {
    minimumFractionDigits: desatinne,
    maximumFractionDigits: desatinne,
  }).format(cislo);
}

function menaNaText(hodnota) {
  const cislo = Number(hodnota);
  if (!Number.isFinite(cislo)) return '—';

  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cislo);
}

function menaAutoNaText(hodnota) {
  const cislo = Number(hodnota);
  if (!Number.isFinite(cislo)) return '—';

  const jeCele = Math.abs(cislo % 1) < 0.000001;

  return new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: jeCele ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cislo);
}

function percentoNaText(hodnota, desatinne = 2) {
  const cislo = Number(hodnota);
  if (!Number.isFinite(cislo)) return '—';

  return `${cisloNaText(cislo, desatinne)} %`;
}

function mesiaceNaText(mesiace) {
  const m = Number(mesiace);
  if (!Number.isFinite(m)) return '—';

  const roky = Math.floor(m / 12);
  const zvysok = m % 12;
  return `${roky} r ${zvysok} m`;
}

function pridajObrazokCover(pdf, imageData, x, y, cielovaSirka, cielovaVyska, zarovnanieX = 'center') {
  const props = pdf.getImageProperties(imageData);
  const povodnaSirka = props.width || 1;
  const povodnaVyska = props.height || 1;

  const scale = Math.max(cielovaSirka / povodnaSirka, cielovaVyska / povodnaVyska);
  const sirka = povodnaSirka * scale;
  const vyska = povodnaVyska * scale;

  let posunX = x;
  if (zarovnanieX === 'center') {
    posunX = x + (cielovaSirka - sirka) / 2;
  } else if (zarovnanieX === 'right') {
    posunX = x + (cielovaSirka - sirka);
  }

  const posunY = y + (cielovaVyska - vyska) / 2;

  pdf.addImage(imageData, 'PNG', posunX, posunY, sirka, vyska);
}

function zapisKontaktVPate(pdf, kontaktPdf = {}) {
  const meno = hodnotaText(kontaktPdf?.meno, '');
  const email = hodnotaText(kontaktPdf?.email, '');
  const telefon = hodnotaText(kontaktPdf?.telefon, '');
  const nbsCislo = hodnotaText(kontaktPdf?.nbsCislo, '');

  const riadky = [
    { text: 'Kontakt:', tucne: true },
    { text: meno, tucne: false },
    { text: email ? `mail: ${email}` : '', tucne: false },
    { text: telefon ? `tel.: ${telefon}` : '', tucne: false },
    { text: nbsCislo ? `NBS číslo: ${nbsCislo}` : '', tucne: false },
  ].filter((riadok) => String(riadok.text || '').trim());

  if (!riadky.length) return;

  const sirkaBloku = 42;
  const x = SIRKA_STRANY - OKRAJ_X - sirkaBloku;
  const spodneY = VYSKA_STRANY - 4.8;
  const xDivider = x - 3.2;

  const pripraveneRiadky = riadky.flatMap((riadok) => {
    pdf.setFont('Inter', riadok.tucne ? 'bold' : 'normal');
    pdf.setFontSize(8.5);

    const casti = pdf.splitTextToSize(riadok.text, sirkaBloku);
    return casti.map((text) => ({
      text,
      tucne: riadok.tucne,
    }));
  });

  let y = spodneY - ((pripraveneRiadky.length - 1) * 3.9);

  const dividerTopY = y - 1.8;
  const dividerBottomY = y + ((pripraveneRiadky.length - 1) * 3.9) + 0.8;

  pdf.setDrawColor(...FARBA_PATA);
  pdf.setLineWidth(0.45);
  pdf.line(xDivider, dividerTopY, xDivider, dividerBottomY);

  pripraveneRiadky.forEach((riadok) => {
    pdf.setTextColor(...FARBA_PATA);
    pdf.setFont('Inter', riadok.tucne ? 'bold' : 'normal');
    pdf.setFontSize(8.5);
    pdf.text(riadok.text, x, y);
    y += 3.9;
  });
}

function zapisFiremneUdajeVPate(pdf) {
  const riadky = [
    'FinCloud s.r.o.',
    'IČO: 54394139, Kalinčiakova 33A, 831 04 Bratislava',
    'Spoločnosť je zapísaná v registri SFA NBS pod č. 269451',
  ];

  const x = SIRKA_STRANY / 2;
  const spodneY = VYSKA_STRANY - 4.8;
  let y = spodneY - ((riadky.length - 1) * 6.9);
  // let y = VYSKA_STRANY - 11.8;

  riadky.forEach((riadok, index) => {
    pdf.setTextColor(...FARBA_PATA);
    pdf.setFont('Inter', index === 0 ? 'bold' : 'normal');
    pdf.setFontSize(9.0);
    pdf.text(riadok, x, y, { align: 'center' });
    y += 3.8;
  });
}

function pridajHlavuAPatu(pdf, cisloStrany, kontaktPdf = null, pocetStran = null) {
  pridajObrazokCover(pdf, horeImg, 0, 0, SIRKA_STRANY, VYSKA_HLAVY, 'center');
  pridajObrazokCover(pdf, doleImg, 0, VYSKA_STRANY - VYSKA_PATY, SIRKA_STRANY, VYSKA_PATY, 'center');

  if (kontaktPdf) {
    zapisKontaktVPate(pdf, kontaktPdf);
  }

  zapisFiremneUdajeVPate(pdf);

  pdf.setTextColor(...FARBA_JEMNA);
  pdf.setFont('Inter', 'normal');
  pdf.setFontSize(9.0);

  const textStrany = pocetStran ? `Strana ${cisloStrany}/${pocetStran}` : `${cisloStrany}`;
  pdf.text(textStrany, SIRKA_STRANY / 2, VYSKA_STRANY - 4.8, { align: 'center' });
}

function novaStrana(pdf, stav) {
  pdf.addPage();
  stav.cisloStrany += 1;
  pridajHlavuAPatu(pdf, stav.cisloStrany, stav.kontaktPdf, stav.pocetStran || null);
  stav.y = START_Y;
}

function zabezpecMiesto(pdf, stav, potrebnaVyska) {
  if (stav.y + potrebnaVyska <= MAX_Y) return;
  novaStrana(pdf, stav);
}

function kresliLinku(pdf, y) {
  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setLineWidth(0.3);
  pdf.line(OKRAJ_X, y, SIRKA_STRANY - OKRAJ_X, y);
}

function zapisNadpisSekcie(pdf, stav, text) {
  zabezpecMiesto(pdf, stav, 10);

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(13);
  pdf.text(text, OKRAJ_X, stav.y);

  stav.y += 2;
  kresliLinku(pdf, stav.y + 1);
  stav.y += 6;
}

function zapisOdstavec(pdf, stav, text, options = {}) {
  const {
    velkost = 9,
    tučne = false,
    farba = FARBA_TEXT,
    medzeraPo = 2.5,
  } = options;

  const riadky = pdf.splitTextToSize(hodnotaText(text), SIRKA_OBSAHU);
  const vyska = riadky.length * (velkost * 0.48) + 1.5;

  zabezpecMiesto(pdf, stav, vyska);

  pdf.setTextColor(...farba);
  pdf.setFont('Inter', tučne ? 'bold' : 'normal');
  pdf.setFontSize(velkost);
  pdf.text(riadky, OKRAJ_X, stav.y);

  stav.y += vyska + medzeraPo;
}

function zoberRocneRiadkyPoRokoch(riadky = []) {
  if (!Array.isArray(riadky) || !riadky.length) return [];

  const vystup = riadky.filter((riadok) => Number(riadok?.mesiac) % 12 === 0);

  const posledny = riadky[riadky.length - 1];
  const uzObsahujePosledny =
    vystup.some((riadok) => Number(riadok?.mesiac) === Number(posledny?.mesiac));

  if (posledny && !uzObsahujePosledny) {
    vystup.push(posledny);
  }

  return vystup.map((riadok, index) => ({
    rok: index + 1,
    mesiac: Number(riadok?.mesiac) || 0,
    splatkaC: Number(riadok?.splatkaC) || 0,
    istinaC: Number(riadok?.istinaC) || 0,
    urokC: Number(riadok?.urokC) || 0,
    zostatokC: Number(riadok?.zostatokC) || 0,
  }));
}

function zoberRocneBodyInvestovaniaPoRokoch(graf = []) {
  if (!Array.isArray(graf) || !graf.length) return [];

  const rocne = graf
    .map((hodnota, index) => ({
      mesiac: index + 1,
      hodnota: Number(hodnota) || 0,
    }))
    .filter((riadok) => riadok.mesiac % 12 === 0);

  const posledny = {
    mesiac: graf.length,
    hodnota: Number(graf[graf.length - 1]) || 0,
  };

  if (!rocne.some((riadok) => riadok.mesiac === posledny.mesiac)) {
    rocne.push(posledny);
  }

  return rocne.map((riadok, index) => ({
    rok: index + 1,
    mesiac: riadok.mesiac,
    hodnota: riadok.hodnota,
  }));
}

function odhadVyskyPrehladovejKarty(pdf, sirka, riadky = []) {
  let vyska = 8;

  riadky.forEach((riadok) => {
    const odsadenie = Number(riadok?.odsadenie) || 0;
    const vnutornaSirka = sirka - 8 - odsadenie;

    vyska += 3.8;

    if (Array.isArray(riadok?.bloky) && riadok.bloky.length) {
      riadok.bloky.forEach((blok, index) => {
        vyska += 3.4;

        const textRiadky = pdf.splitTextToSize(
          hodnotaText(blok?.text),
          vnutornaSirka
        );

        vyska += Math.max(3.6, textRiadky.length * 3.4);

        if (index < riadok.bloky.length - 1) {
          vyska += 1.1;
        }
      });
    } else {
      const value = hodnotaText(riadok?.value);
      const valueRiadky = pdf.splitTextToSize(value, vnutornaSirka);

      vyska += Math.max(3.8, valueRiadky.length * 3.7);
    }

    vyska += 1.1;
  });

  return Math.max(22, vyska + 2.2);
}

function zapisPrehladovuKartu(pdf, x, y, sirka, nadpis, riadky = [], options = {}) {
  const { minVyska = 0, odsadenieHodnotX = 0 } = options;
  const vypocitanaVyska = odhadVyskyPrehladovejKarty(pdf, sirka, riadky);
  const vyska = Math.max(vypocitanaVyska, minVyska);

  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, sirka, vyska, 3, 3, 'FD');

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(9.2);
  pdf.text(hodnotaText(nadpis), x + 4, y + 6);

  let lokalneY = y + 10;

  riadky.forEach((riadok) => {
    const odsadenie = Number(riadok?.odsadenie) || 0;
    const textX = x + 4 + odsadenie;
    const vnutornaSirka = sirka - 8 - odsadenie;

    pdf.setTextColor(...FARBA_JEMNA);
    pdf.setFont('Inter', riadok?.labelTucne ? 'bold' : 'normal');
    pdf.setFontSize(7.2);
    pdf.text(hodnotaText(riadok?.label), textX, lokalneY);

    lokalneY += 3.7;

    if (Array.isArray(riadok?.bloky) && riadok.bloky.length) {
      riadok.bloky.forEach((blok, index) => {
        pdf.setTextColor(...FARBA_TEXT);
        pdf.setFont('Inter', 'bold');
        pdf.setFontSize(8.0);
        pdf.text(hodnotaText(blok?.nadpis), textX, lokalneY);

        lokalneY += 3.5;

        pdf.setTextColor(...FARBA_TEXT);
        pdf.setFont('Inter', 'normal');
        pdf.setFontSize(7.9);

        const textRiadky = pdf.splitTextToSize(
          hodnotaText(blok?.text),
          vnutornaSirka
        );

        pdf.text(textRiadky, textX, lokalneY);
        lokalneY += Math.max(3.6, textRiadky.length * 3.4);

        if (index < riadok.bloky.length - 1) {
          lokalneY += 1.1;
        }
      });
    } else {
      pdf.setTextColor(...FARBA_TEXT);
      pdf.setFont('Inter', riadok?.valueTucne ? 'bold' : 'normal');
      pdf.setFontSize(8.0);

      const valueRiadky = pdf.splitTextToSize(
        hodnotaText(riadok?.value),
        vnutornaSirka
      );

      pdf.text(valueRiadky, textX + odsadenieHodnotX, lokalneY);
      lokalneY += Math.max(3.6, valueRiadky.length * 3.5);
    }

    lokalneY += 1.0;
  });

  return vyska;
}

function zapisGrafDoKarty(pdf, x, y, sirka, vyska, nadpis, grafBase64) {
  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, sirka, vyska, 3, 3, 'FD');

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(9.2);
  pdf.text(hodnotaText(nadpis), x + 4, y + 6);

  if (!grafBase64) {
    pdf.setTextColor(...FARBA_JEMNA);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(8);
    pdf.text('Graf nie je dostupný.', x + 4, y + 15);
    return vyska;
  }

  const obrazokX = x + 4;
  const obrazokY = y + 9;
  const obrazokSirka = sirka - 8;
  const obrazokVyska = vyska - 13;

  const props = pdf.getImageProperties(grafBase64);
  const pomer = Math.min(
    obrazokSirka / props.width,
    obrazokVyska / props.height
  );

  const sirkaGrafu = props.width * pomer;
  const vyskaGrafu = props.height * pomer;
  const posunX = obrazokX + (obrazokSirka - sirkaGrafu) / 2;
  const posunY = obrazokY + (obrazokVyska - vyskaGrafu) / 2;

  pdf.addImage(grafBase64, 'PNG', posunX, posunY, sirkaGrafu, vyskaGrafu);

  return vyska;
}

function zapisFincloudTipDoKarty(pdf, x, y, sirka, vyska, dataPrePdf) {
  const tipy = Array.isArray(dataPrePdf?.fincloudTip?.tipy) ? dataPrePdf.fincloudTip.tipy : [];
  if (!tipy.length || vyska <= 12) return 0;

  const zoradeneTipy = [...tipy]
    .sort((a, b) => (Number(b?.rokyCiel) || 0) - (Number(a?.rokyCiel) || 0))
    .slice(0, 2);

  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, sirka, vyska, 3, 3, 'FD');

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(9.2);
  pdf.text('FinCloud Tip', x + 4, y + 6);

  const vnutornaMedzera = 4;
  const yKarty = y + 11;
  //const vyskaPodkarty = Math.max(16, vyska - 15);
  const vyskaPodkarty = 26;

  const pocetKariet = zoradeneTipy.length === 1 ? 1 : 2;
  const sirkaPodkarty = pocetKariet === 1
    ? sirka - 8
    : (sirka - 8 - vnutornaMedzera) / 2;

  zoradeneTipy.forEach((tip, index) => {
    const kartaX = x + 4 + index * (sirkaPodkarty + vnutornaMedzera);

    pdf.setDrawColor(...FARBA_LINKA);
    pdf.setFillColor(248, 245, 238);
    pdf.roundedRect(kartaX, yKarty, sirkaPodkarty, vyskaPodkarty, 2.4, 2.4, 'FD');

    pdf.setFillColor(201, 184, 136);
    pdf.roundedRect(kartaX, yKarty + 2, 1.2, vyskaPodkarty - 4, 0.6, 0.6, 'F');

    const veta = [
      `Investujte ${menaAutoNaText(tip?.mesacne)} mesačne`,
      `a hypotéku môžete splatiť`,
      `o ${hodnotaText(tip?.usetreneRoky)} rokov skôr`,
    ];

    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(8.0);

    const textMaxSirka = sirkaPodkarty - 10;
    const riadky = veta.flatMap((cast) => pdf.splitTextToSize(cast, textMaxSirka));

    const vyskaTextu = riadky.length * 4.1;
    let textY = yKarty + (vyskaPodkarty - vyskaTextu) / 2 + 3.2;

    riadky.forEach((riadok, riadokIndex) => {
      const jeZlaty = riadok.includes(menaAutoNaText(tip?.mesacne)) || riadok.includes(`${hodnotaText(tip?.usetreneRoky)} rokov`);

      pdf.setFont('Inter', jeZlaty ? 'bold' : 'normal');
      pdf.setTextColor(...(jeZlaty ? FARBA_NADPIS : FARBA_TEXT));
      pdf.text(riadok, kartaX + sirkaPodkarty / 2, textY + riadokIndex * 4.1, { align: 'center' });
    });
  });

  pdf.setTextColor(...FARBA_JEMNA);
  pdf.setFont('Inter', 'normal');
  pdf.setFontSize(8.2);
  pdf.text('Pri priemernom ročnom zhodnotení 6%.', x + sirka / 2, y + vyska - 5, { align: 'center' });

  return vyska;
}

function odhadVyskySekcnejKolony(pdf, riadky = [], sirka) {
  const sirkaLabel = sirka * 0.56;
  const sirkaHodnoty = sirka * 0.40;
  const vyskaRiadku = 3.9;
  const medzeraPo = 1.2;

  return (riadky || []).filter(Boolean).reduce((suma, riadok) => {
    const labelRiadky = pdf.splitTextToSize(hodnotaText(riadok?.label), sirkaLabel);
    const valueRiadky = pdf.splitTextToSize(hodnotaText(riadok?.value), sirkaHodnoty);

    return suma + Math.max(labelRiadky.length, valueRiadky.length) * vyskaRiadku + medzeraPo;
  }, 0);
}

function vykresliSekcnuKolonu(pdf, x, y, sirka, riadky = []) {
  let lokalneY = y;

  const sirkaLabel = sirka * 0.56;
  const sirkaHodnoty = sirka * 0.40;
  const vyskaRiadku = 3.9;
  const medzeraPo = 1.2;

  (riadky || []).filter(Boolean).forEach((riadok) => {
    const labelRiadky = pdf.splitTextToSize(hodnotaText(riadok?.label), sirkaLabel);
    const valueRiadky = pdf.splitTextToSize(hodnotaText(riadok?.value), sirkaHodnoty);

    pdf.setTextColor(...FARBA_JEMNA);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(8.1);
    pdf.text(labelRiadky, x, lokalneY);

    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'bold');
    pdf.setFontSize(8.4);
    pdf.text(valueRiadky, x + sirka, lokalneY, { align: 'right' });

    const pocetRiadkov = Math.max(labelRiadky.length, valueRiadky.length);
    lokalneY += pocetRiadkov * vyskaRiadku + medzeraPo;
  });

  return lokalneY;
}

function zapisSekcnuKartuDveKolony(pdf, stav, nadpis, options = {}) {
  const {
    nadpisVpravo = '',
    lavyRiadky = [],
    pravyRiadky = [],
    spodnyText = '',
  } = options;

  const medzeraStlpcov = 8;
  const sirkaKarty = SIRKA_OBSAHU;
  const sirkaObsahu = sirkaKarty - 8;
  const sirkaLavyStlpec = (sirkaObsahu - medzeraStlpcov) * 0.45;
  const sirkaPravyStlpec = (sirkaObsahu - medzeraStlpcov) * 0.55;

  const vyskaLavy = odhadVyskySekcnejKolony(pdf, lavyRiadky, sirkaLavyStlpec);
  const vyskaPravy = odhadVyskySekcnejKolony(pdf, pravyRiadky, sirkaPravyStlpec);

  const vyskaSpodnyText = spodnyText
    ? pdf.splitTextToSize(spodnyText, sirkaKarty - 8).length * 4.1 + 4
    : 0;

  const vyskaKarty = Math.max(32, 12 + Math.max(vyskaLavy, vyskaPravy) + vyskaSpodnyText + 6);

  zabezpecMiesto(pdf, stav, vyskaKarty + 4);

  const x = OKRAJ_X;
  const y = stav.y;

  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(x, y, sirkaKarty, vyskaKarty, 3, 3, 'FD');

  pdf.setTextColor(...FARBA_TEXT);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(11.2);
  pdf.text(nadpis.toUpperCase(), x + 4, y + 7);

  if (nadpisVpravo) {
    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'bold');
    pdf.setFontSize(8.8);
    pdf.text(hodnotaText(nadpisVpravo), x + sirkaKarty - 4, y + 7, { align: 'right' });
  }

  const obsahY = y + 14;
  const lavyX = x + 4;
  const pravyX = lavyX + sirkaLavyStlpec + medzeraStlpcov;

  const koniecLavy = vykresliSekcnuKolonu(pdf, lavyX, obsahY, sirkaLavyStlpec, lavyRiadky);
  const koniecPravy = vykresliSekcnuKolonu(pdf, pravyX, obsahY, sirkaPravyStlpec, pravyRiadky);

  let spodokY = Math.max(koniecLavy, koniecPravy);

  if (spodnyText) {
    const riadky = pdf.splitTextToSize(spodnyText, sirkaKarty - 8);
    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(8.6);
    pdf.text(riadky, x + 4, spodokY + 2);
  }

  stav.y += vyskaKarty + 4;
}

function zapisPrvuStranuPrehladu(pdf, stav, dataPrePdf) {
  const prehlad = dataPrePdf?.prehladPdf;
  if (!prehlad) return;

  const medzera = 4;
  const yStart = stav.y;

  const sirkaMalejKarty = (SIRKA_OBSAHU - medzera * 2) / 3;
  const x1 = OKRAJ_X;
  const x2 = x1 + sirkaMalejKarty + medzera;
  const x3 = x2 + sirkaMalejKarty + medzera;

  const sirkaParametre = sirkaMalejKarty;
  const sirkaGraf = SIRKA_OBSAHU - sirkaParametre - medzera;

  const vyskaParametre = zapisPrehladovuKartu(
    pdf,
    OKRAJ_X,
    yStart,
    sirkaParametre,
    'Parametre Hypotéky',
    prehlad.parametreHypotekyPdf || [],
    { odsadenieHodnotX: 1.1 }
  );

  const vyskaGrafu = Math.max(vyskaParametre, 58);

  zapisGrafDoKarty(
    pdf,
    x2,
    yStart,
    sirkaGraf,
    vyskaGrafu,
    'Graf',
    prehlad.grafBase64
  );

  const prvyRiadokVyska = Math.max(vyskaParametre, vyskaGrafu);
  const yDruhyRiadok = yStart + prvyRiadokVyska + medzera;

  const odhadVyskaLimity = odhadVyskyPrehladovejKarty(
    pdf,
    sirkaMalejKarty,
    prehlad.limityPdf || []
  );

  const vyskaLimity = zapisPrehladovuKartu(
    pdf,
    x1,
    yDruhyRiadok,
    sirkaMalejKarty,
    'Limity DTI / DSTI',
    prehlad.limityPdf || [],
    { odsadenieHodnotX: 1.1 }
  );

  const vyskaZiadatelia = zapisPrehladovuKartu(
    pdf,
    x2,
    yDruhyRiadok,
    sirkaMalejKarty,
    'Žiadateľ / Žiadatelia',
    prehlad.ziadateliaPrehladPdf || [],
    { minVyska: odhadVyskaLimity }
  );

  const vyskaZavazky = zapisPrehladovuKartu(
    pdf,
    x3,
    yDruhyRiadok,
    sirkaMalejKarty,
    'Celkové záväzky',
    prehlad.zavazkyPrehladPdf || []
  );

  const vyskaLavyBlok = Math.max(vyskaLimity, vyskaZiadatelia);
  const yTip = yDruhyRiadok + vyskaLavyBlok + medzera;
  const vyskaTip = FIXNA_VYSKA_FINCLOUD_TIP;

  if ((Array.isArray(dataPrePdf?.fincloudTip?.tipy) ? dataPrePdf.fincloudTip.tipy.length : 0) > 0) {
    zapisFincloudTipDoKarty(
      pdf,
      x1,
      yTip,
      sirkaMalejKarty * 2 + medzera,
      vyskaTip,
      dataPrePdf
    );
  }

  stav.y = yDruhyRiadok + Math.max(vyskaZavazky, vyskaLavyBlok + medzera + vyskaTip) + 5;
}

function vytvorZlucenyKalendar(dataPrePdf) {
  const bezny = zoberRocneRiadkyPoRokoch(dataPrePdf?.splatkovyKalendar?.bezny || []);

  const zobrazitPredcasneSplatenie = Boolean(
    dataPrePdf?.splatkovyKalendar?.zobrazitPredcasneSplatenie
  );

  const zobrazitInvestovanie = Boolean(
    dataPrePdf?.splatkovyKalendar?.zobrazitInvestovanie
  );

  const sPS = zobrazitPredcasneSplatenie
    ? zoberRocneRiadkyPoRokoch(dataPrePdf?.splatkovyKalendar?.sPredcasnymSplatenim || [])
    : [];

  const investovanie = zobrazitInvestovanie
    ? zoberRocneBodyInvestovaniaPoRokoch(
        dataPrePdf?.investovanie?.investicnyPriebeh?.graf || []
      )
    : [];

  const maxRiadkov = Math.max(bezny.length, sPS.length, investovanie.length);
  const vystup = [];

  for (let i = 0; i < maxRiadkov; i += 1) {
    const zaklad = bezny[i] || null;
    const ps = sPS[i] || null;
    const inv = investovanie[i] || null;

    vystup.push({
      rok: zaklad?.rok ?? ps?.rok ?? inv?.rok ?? i + 1,
      splatkaC: zaklad?.splatkaC ?? null,
      istinaC: zaklad?.istinaC ?? null,
      urokC: zaklad?.urokC ?? null,
      zostatokC: zaklad?.zostatokC ?? null,
      spsZostatokC: zobrazitPredcasneSplatenie ? (ps?.zostatokC ?? null) : null,
      investicia: zobrazitInvestovanie ? (inv?.hodnota ?? null) : null,
    });
  }

  return {
    riadky: vystup,
    zobrazitPredcasneSplatenie,
    zobrazitInvestovanie,
  };
}

function zapisTabulkuKalendar(pdf, stav, kalendarData = []) {
  const data = Array.isArray(kalendarData) ? { riadky: kalendarData } : (kalendarData || {});
  const riadky = Array.isArray(data.riadky) ? data.riadky : [];

  if (!riadky.length) return;

  const zobrazitPredcasneSplatenie = Boolean(data.zobrazitPredcasneSplatenie);
  const zobrazitInvestovanie = Boolean(data.zobrazitInvestovanie);

  const vyskaRiadku = 5.4;
  const sirkaTabulky = SIRKA_OBSAHU;

  const stlpce = [
    { key: 'rok', label: 'Rok', sirka: 15, align: 'left' },
    { key: 'splatkaC', label: 'Splátka', sirka: 24, align: 'left' },
    { key: 'istinaC', label: 'Istina', sirka: 24, align: 'left' },
    { key: 'urokC', label: 'Úrok', sirka: 22, align: 'left' },
    { key: 'zostatokC', label: 'Zostatok', sirka: 30, align: 'left' },
    ...(zobrazitPredcasneSplatenie
      ? [{ key: 'spsZostatokC', label: 'S PS zostatok', sirka: 30, align: 'left' }]
      : []),
    ...(zobrazitInvestovanie
      ? [{ key: 'investicia', label: 'Investícia', sirka: 26, align: 'left' }]
      : []),
  ];

  const sucetSiriek = stlpce.reduce((sum, stlpec) => sum + stlpec.sirka, 0);
  const koeficient = sirkaTabulky / sucetSiriek;

  const stlpcePrepocitane = stlpce.map((stlpec) => ({
    ...stlpec,
    sirka: stlpec.sirka * koeficient,
  }));

  const vykresliHlavicku = () => {
    zabezpecMiesto(pdf, stav, 8);

    pdf.setFillColor(246, 242, 234);
    pdf.roundedRect(OKRAJ_X, stav.y - 4.5, SIRKA_OBSAHU, 7, 1.8, 1.8, 'F');

    pdf.setTextColor(...FARBA_NADPIS);
    pdf.setFont('Inter', 'bold');
    pdf.setFontSize(7.7);

    let x = OKRAJ_X + 1.6;
    stlpcePrepocitane.forEach((stlpec) => {
      pdf.text(stlpec.label, x, stav.y);
      x += stlpec.sirka;
    });

    stav.y += 5.8;
  };

  vykresliHlavicku();

  riadky.forEach((riadok) => {
    zabezpecMiesto(pdf, stav, vyskaRiadku + 1);

    if (stav.y + vyskaRiadku > MAX_Y) {
      novaStrana(pdf, stav);
      vykresliHlavicku();
    }

    pdf.setDrawColor(236, 231, 220);
    pdf.setLineWidth(0.2);
    pdf.line(OKRAJ_X, stav.y + 1.2, OKRAJ_X + SIRKA_OBSAHU, stav.y + 1.2);

    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(7.8);

    let x = OKRAJ_X + 1.6;

    stlpcePrepocitane.forEach((stlpec) => {
      let text = '—';

      if (stlpec.key === 'rok') {
        text = hodnotaText(riadok?.rok);
      }

      if (stlpec.key === 'splatkaC') {
        text = hodnotaText(riadok?.splatkaC !== null ? menaNaText(riadok.splatkaC / 100) : '—');
      }

      if (stlpec.key === 'istinaC') {
        text = hodnotaText(riadok?.istinaC !== null ? menaNaText(riadok.istinaC / 100) : '—');
      }

      if (stlpec.key === 'urokC') {
        text = hodnotaText(riadok?.urokC !== null ? menaNaText(riadok.urokC / 100) : '—');
      }

      if (stlpec.key === 'zostatokC') {
        text = hodnotaText(riadok?.zostatokC !== null ? menaNaText(riadok.zostatokC / 100) : '—');
      }

      if (stlpec.key === 'spsZostatokC') {
        text = hodnotaText(riadok?.spsZostatokC !== null ? menaNaText(riadok.spsZostatokC / 100) : '—');
      }

      if (stlpec.key === 'investicia') {
        text = hodnotaText(riadok?.investicia !== null ? menaNaText(riadok.investicia) : '—');
      }

      pdf.text(text, x, stav.y);
      x += stlpec.sirka;
    });

    stav.y += vyskaRiadku;
  });

  stav.y += 1.5;
}

function zapisPredcasneSplatenie(pdf, stav, dataPrePdf) {
  const sekcia = dataPrePdf?.predcasneSplatenie;
  if (!sekcia?.aktivne) return;

  const lavyRiadky = [
    { label: 'Jednorázovo', value: menaAutoNaText(sekcia.jednoraz) },
    { label: 'Pravidelne mesačne', value: menaAutoNaText(sekcia.mesacne) },
    { label: 'Pravidelne ročne', value: menaAutoNaText(sekcia.rocne) },
    { label: 'Začiatok (mesiac)', value: hodnotaText((Number(sekcia.start) || 0) + 1) },
    { label: 'Počet opakovaní (ročne)', value: hodnotaText(sekcia.opakovania) },
  ];

  const pravyRiadky = sekcia.rezim === 'skratenie'
    ? [
        {
          label: 'Predčasné splatenie hypotéky',
          value: sekcia?.vysledok?.payoffMonth ? `${mesiaceNaText(sekcia.vysledok.payoffMonth)} (m: ${sekcia.vysledok.payoffMonth})` : '—',
        },
        {
          label: 'Splatené skôr o',
          value: sekcia?.vysledok?.monthsSaved ? mesiaceNaText(sekcia.vysledok.monthsSaved) : '—',
        },
        {
          label: 'Celkové preplatenie bez PS',
          value: sekcia?.vysledok?.totalPaidBase ? menaAutoNaText(sekcia.vysledok.totalPaidBase) : '—',
        },
        {
          label: 'Celkové preplatenie s PS',
          value: sekcia?.vysledok?.totalPaidPP ? menaAutoNaText(sekcia.vysledok.totalPaidPP) : '—',
        },
        {
          label: '↳ úspora',
          value: (sekcia?.vysledok?.totalPaidBase !== null && sekcia?.vysledok?.totalPaidPP !== null)
            ? menaAutoNaText((Number(sekcia.vysledok.totalPaidBase) || 0) - (Number(sekcia.vysledok.totalPaidPP) || 0))
            : '—',
          sub: true,
        },
      ]
    : [
        {
          label: 'Nová splátka po 1. PS',
          value: sekcia?.vysledok?.firstNewPayment ? menaAutoNaText(sekcia.vysledok.firstNewPayment) : '—',
        },
        {
          label: 'Priemerná splátka',
          value: sekcia?.vysledok?.avgPayment ? menaAutoNaText(sekcia.vysledok.avgPayment) : '—',
        },
        {
          label: 'Predčasné splatenie hypotéky',
          value: sekcia?.vysledok?.payoffMonth ? `${mesiaceNaText(sekcia.vysledok.payoffMonth)} (m: ${sekcia.vysledok.payoffMonth})` : '—',
        },
        {
          label: 'Splatené skôr o',
          value: sekcia?.vysledok?.monthsSaved ? mesiaceNaText(sekcia.vysledok.monthsSaved) : '—',
        },
        {
          label: 'Celkové preplatenie hypotéky bez PS',
          value: sekcia?.vysledok?.totalPaidBase ? menaAutoNaText(sekcia.vysledok.totalPaidBase) : '—',
        },
        {
          label: 'Celkové preplatenie hypotéky s PS',
          value: sekcia?.vysledok?.totalPaidPP ? menaAutoNaText(sekcia.vysledok.totalPaidPP) : '—',
        },
        {
          label: '↳ úspora',
          value: (sekcia?.vysledok?.totalPaidBase !== null && sekcia?.vysledok?.totalPaidPP !== null)
            ? menaAutoNaText((Number(sekcia.vysledok.totalPaidBase) || 0) - (Number(sekcia.vysledok.totalPaidPP) || 0))
            : '—',
          sub: true,
        },
      ];

  const spodnyText = Array.isArray(sekcia.exceededRelevant) && sekcia.exceededRelevant.length
    ? `Prekročenie 30 % limitu bez poplatku v roku: ${[...new Set(sekcia.exceededRelevant.map((e) => Number(e.year) + 1))].join(', ')}`
    : 'Ročný 30 % limit bez poplatku nebol prekročený';

  zapisSekcnuKartuDveKolony(pdf, stav, 'Predčasné splatenie', {
    nadpisVpravo: sekcia.rezim === 'skratenie' ? '(skrátenie splatnosti)' : '(zníženie splátky)',
    lavyRiadky,
    pravyRiadky,
    spodnyText,
  });
}

function zapisInvestovanie(pdf, stav, dataPrePdf) {
  const sekcia = dataPrePdf?.investovanie;
  if (!sekcia?.aktivne) return;

  const profilText = sekcia.profil
    ? `${hodnotaText(sekcia.profil).charAt(0).toUpperCase()}${hodnotaText(sekcia.profil).slice(1)}`
    : '—';

  const lavyRiadky = [
    { label: 'Investičný profil', value: profilText },
    { label: 'Jednorázovo', value: menaAutoNaText(sekcia.jednoraz) },
    { label: 'Mesačne', value: menaAutoNaText(sekcia.mesacne) },
    { label: 'Zhodnotenie (p.a.)', value: percentoNaText(sekcia.zhodnotenie) },
    { label: 'Horizont (r)', value: `${hodnotaText(sekcia.horizont)} r.` },
  ];

  const pravyRiadky = !sekcia.vystupPS
    ? [ 
        {
          label: 'Priemerné ročné zhodnotenie',
          value: `${cisloNaText(sekcia.zhodnotenie, 2)} % p.a.`,
        },
        {
          label: `Stav sporenia/investície po ${hodnotaText(sekcia.horizont)} r`,
          value: sekcia?.vysledok?.konecnaHodnota ? menaAutoNaText(sekcia.vysledok.konecnaHodnota) : '—',
        },
        {
          label: '↳ z toho vklady',
          value: sekcia?.vysledok?.vlozeneProstriedky ? menaAutoNaText(sekcia.vysledok.vlozeneProstriedky) : '—',
          sub: true,
        },
        {
          label: '↳ z toho výnos',
          value: sekcia?.vysledok?.vynos ? menaAutoNaText(sekcia.vysledok.vynos) : '—',
          sub: true,
        },
      ]
    : [
        {
          label: 'Predčasné splatenie hypotéky',
          value: sekcia?.vysledok?.mesiacSplatenia ? `${mesiaceNaText(sekcia.vysledok.mesiacSplatenia)} (m: ${sekcia.vysledok.mesiacSplatenia})` : '—',
        },
        {
          label: 'Splatené skôr o',
          value: sekcia?.vysledok?.usetreneMesiace ? mesiaceNaText(sekcia.vysledok.usetreneMesiace) : '—',
        },
        {
          label: 'Zostatok sporenia / investície po PS',
          value: sekcia?.vysledok?.zostatokPoSplateni !== null ? menaAutoNaText(sekcia.vysledok.zostatokPoSplateni) : '—',
        },
        {
          label: 'Priemerné ročné zhodnotenie',
          value: `${cisloNaText(sekcia.zhodnotenie, 2)} % p.a.`,
        },
      ];

  zapisSekcnuKartuDveKolony(pdf, stav, 'Sporenie / investovanie', {
    nadpisVpravo: sekcia.vystupPS
      ? 'Investovanie s predčasným splatením'
      : 'Investovanie bez predčasného splatenia',
    lavyRiadky,
    pravyRiadky,
  });
}

function jeCisloPdf(v) {
  return Number.isFinite(Number(v));
}

function fixTextPorovnavacPdf(banka, fixaciaRoky) {
  const jeAtipickaFixacia = banka?.jeAtipickaFixacia === true;
  const fixaciaMesiace = jeAtipickaFixacia ? (Number(banka?.fixaciaMesiace) || null) : null;

  if (jeAtipickaFixacia && fixaciaMesiace) {
    return ` (${fixaciaMesiace}Mes Fix)`;
  }

  if (Number.isFinite(Number(fixaciaRoky)) && Number(fixaciaRoky) > 0) {
    return ` (${Number(fixaciaRoky)}R Fix)`;
  }

  return '';
}

function zapisPorovnavacBankuKartuPdf(pdf, x, y, sirka, vyska, banka, sekcia) {
  const bankaNazov = hodnotaText(banka?.bankaKod ?? banka?.bankaNazov ?? 'Banka', 'Banka');

  const finalRate = jeCisloPdf(banka?.finalRate) ? Number(banka.finalRate) : null;
  const splatka = jeCisloPdf(banka?.splatka) ? Number(banka.splatka) : null;
  const stressSplatka = jeCisloPdf(banka?.stressSplatka) ? Number(banka.stressSplatka) : null;
  const spracPoplatok = jeCisloPdf(banka?.spracPoplatok) ? Number(banka.spracPoplatok) : null;
  const spracPoplatokText = banka?.spracPoplatokText ? String(banka.spracPoplatokText) : null;
  const maxVyskaUveru = jeCisloPdf(banka?.maxVyskaUveru) ? Number(banka.maxVyskaUveru) : null;
  const dstiMaxSplatka = jeCisloPdf(banka?.dstiMaxSplatka) ? Number(banka.dstiMaxSplatka) : null;

  const fixText = fixTextPorovnavacPdf(banka, sekcia?.fixacia);

  const jeNedostupna =
    banka?.nedostupnost?.nemaFixaciu === true ||
    banka?.nedostupnost?.nemaTypHypoteky === true ||
    banka?.nedostupnost?.nemaTypHypotekyPreBanku === true ||
    banka?.nedostupnost?.nemaFixaciuPreBanku === true;

  pdf.setDrawColor(...FARBA_LINKA);
  pdf.setFillColor(248, 245, 238);
  pdf.roundedRect(x, y, sirka, vyska, 3, 3, 'FD');

  pdf.setTextColor(...FARBA_TEXT);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(9.3);
  pdf.text(bankaNazov, x + 3, y + 6);

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(8.2);
  pdf.text(
    finalRate !== null ? `${percentoNaText(finalRate, 2)}${fixText}` : fixText.trim(),
    x + sirka - 3,
    y + 6,
    { align: 'right' }
  );

  if (jeNedostupna) {
    const nedostupnostText = [];
    if (banka?.nedostupnost?.nemaTypHypoteky === true || banka?.nedostupnost?.nemaTypHypotekyPreBanku === true) {
      nedostupnostText.push('Banka neponúka daný typ hypotéky.');
    }
    if (banka?.nedostupnost?.nemaFixaciu === true || banka?.nedostupnost?.nemaFixaciuPreBanku === true) {
      nedostupnostText.push('Banka neponúka daný typ fixácie.');
    }

    pdf.setTextColor(...FARBA_JEMNA);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(7.7);
    const riadky = pdf.splitTextToSize(nedostupnostText.join(' '), sirka - 6);
    pdf.text(riadky, x + 3, y + 14);
    return;
  }

  const lavyX = x + 3;
  const pravyX = x + sirka * 0.5 + 1;

  const zapisMetriku = (xM, yM, label, value) => {
    pdf.setTextColor(...FARBA_JEMNA);
    pdf.setFont('Inter', 'normal');
    pdf.setFontSize(7.4);
    pdf.text(label, xM, yM);

    pdf.setTextColor(...FARBA_TEXT);
    pdf.setFont('Inter', 'bold');
    pdf.setFontSize(8.4);
    pdf.text(value, xM, yM + 4);
  };

  zapisMetriku(lavyX, y + 13, 'Sprac. poplatok', spracPoplatokText ? spracPoplatokText : (spracPoplatok !== null ? menaAutoNaText(spracPoplatok) : '—'));
  zapisMetriku(pravyX, y + 13, 'Výška úveru', jeCisloPdf(sekcia?.suma) ? menaAutoNaText(sekcia.suma) : '—');

  zapisMetriku(lavyX, y + 22.5, 'LTV', jeCisloPdf(sekcia?.ltvPercenta) ? percentoNaText(sekcia.ltvPercenta, 1) : '—');
  zapisMetriku(pravyX, y + 22.5, 'Splatnosť', jeCisloPdf(sekcia?.roky) ? `${Number(sekcia.roky)} rokov` : '—');

  zapisMetriku(lavyX, y + 32, 'Splátka', splatka !== null ? menaAutoNaText(splatka) : '—');
  zapisMetriku(pravyX, y + 32, 'Stress splátka', stressSplatka !== null ? menaAutoNaText(stressSplatka) : '—');

  zapisMetriku(lavyX, y + 41.5, 'Max. výška úveru', maxVyskaUveru !== null ? menaAutoNaText(maxVyskaUveru) : '—');
  zapisMetriku(pravyX, y + 41.5, 'Max. splátka (DSTI)', dstiMaxSplatka !== null ? menaAutoNaText(dstiMaxSplatka) : '—');
}

function zapisPorovnavac(pdf, stav, dataPrePdf) {
  const sekcia = dataPrePdf?.porovnavac;
  if (!sekcia?.maData) return;

  const banky = Array.isArray(sekcia.zobrazeneBanky) ? sekcia.zobrazeneBanky : [];
  if (!banky.length) return;

  novaStrana(pdf, stav);

  zapisNadpisSekcie(pdf, stav, 'Porovnávač bánk');

  const medzeraX = 4;
  const medzeraY = 5;
  const pocetStlpcov = 3;
  const sirkaKarty = (SIRKA_OBSAHU - medzeraX * (pocetStlpcov - 1)) / pocetStlpcov;
  const vyskaKarty = 48;

  banky.forEach((banka, index) => {
    const riadok = Math.floor(index / pocetStlpcov);
    const stlpec = index % pocetStlpcov;

    const x = OKRAJ_X + stlpec * (sirkaKarty + medzeraX);
    const y = stav.y + riadok * (vyskaKarty + medzeraY);

    if (y + vyskaKarty > MAX_Y) {
      novaStrana(pdf, stav);
      const novyIndex = index;
      const novyStlpec = novyIndex % pocetStlpcov;
      const noveX = OKRAJ_X + novyStlpec * (sirkaKarty + medzeraX);
      const noveY = stav.y;

      zapisPorovnavacBankuKartuPdf(pdf, noveX, noveY, sirkaKarty, vyskaKarty, banka, sekcia);
      stav.y = noveY + vyskaKarty + medzeraY;
      return;
    }

    zapisPorovnavacBankuKartuPdf(pdf, x, y, sirkaKarty, vyskaKarty, banka, sekcia);
  });

  const pocetRiadkov = Math.ceil(banky.length / pocetStlpcov);
  stav.y += pocetRiadkov * (vyskaKarty + medzeraY);
}

function zapisPoznamku(pdf, stav, dataPrePdf) {
  const sekcia = dataPrePdf?.poznamka;
  if (!sekcia?.maText) return;

  zapisNadpisSekcie(pdf, stav, 'Poznámka');
  zapisOdstavec(pdf, stav, sekcia.text);
}

function zapisUvod(pdf, stav, dataPrePdf) {
  const menoKlienta = dataPrePdf?.metadata?.menoKlienta || '';
  const datumVyhotovenia = dataPrePdf?.metadata?.datumVyhotovenia || '';

  pdf.setTextColor(...FARBA_NADPIS);
  pdf.setFont('Inter', 'bold');
  pdf.setFontSize(18);
  pdf.text('Informatívna ponuka', OKRAJ_X, stav.y);
  stav.y += 10;

  pdf.setTextColor(...FARBA_TEXT);
  pdf.setFont('Inter', 'normal');
  pdf.setFontSize(10.5);

  if (menoKlienta) {
    pdf.text(`Klient: ${menoKlienta}`, OKRAJ_X, stav.y);
    stav.y += 6;
  }

  pdf.text(`Dátum vyhotovenia: ${datumVyhotovenia}`, OKRAJ_X, stav.y);
  stav.y += 6;

}

function zapisDisclaimer(pdf, stav) {
  const text =
    'Jedná sa o orientačný výpočet a nezáväzný prehľad spracovaný na základe klietom poskytnutých informácií a ponúk bežne dostupných na finančnom trhu. Finálne podmienky financovania najmä výška úveru, úroková sadzba, poplatky a podmienky poskytnutia úveru závisia od individuálneho posúdenia bankou a od predložených podkladov klienta.';

  const sirkaTextu = SIRKA_OBSAHU - 14;

  pdf.setTextColor(...FARBA_JEMNA);
  pdf.setFont('Inter', 'normal');
  pdf.setFontSize(10.5);

  const riadky = pdf.splitTextToSize(text, sirkaTextu);
  const vyskaTextu = riadky.length * 5.2 + 1;
  const potrebnaVyska = 10 + vyskaTextu;

  if (stav.y + 6 + potrebnaVyska > MAX_Y) {
    novaStrana(pdf, stav);
  }

  stav.y = Math.max(stav.y + 6, MAX_Y - potrebnaVyska);

  zapisNadpisSekcie(pdf, stav, 'Dôležité upozornenie');

  pdf.setTextColor(...FARBA_JEMNA);
  pdf.setFont('Inter', 'normal');
  pdf.setFontSize(10.5);
  pdf.text(riadky, OKRAJ_X, stav.y);

  stav.y += riadky.length * 5.2 + 1;
}

export async function exportPonukaPdf(dataPrePdf, exportConfig = {}) {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });
  await nacitajPdfFonty(pdf);

  const menoKlienta = dataPrePdf?.metadata?.menoKlienta || '';
  const stav = {
    y: START_Y,
    cisloStrany: 1,
    menoKlienta,
    kontaktPdf: dataPrePdf?.kontaktPdf || null,
  };

  pridajHlavuAPatu(pdf, stav.cisloStrany, stav.kontaktPdf);

  zapisUvod(pdf, stav, dataPrePdf);
  zapisPrvuStranuPrehladu(pdf, stav, dataPrePdf);

  const maDalsieSekcie =
    Boolean(exportConfig.zahrnutPredcasneSplatenie) ||
    Boolean(exportConfig.zahrnutInvestovanie) ||
    Boolean(exportConfig.zahrnutSplatkovyKalendar) ||
    Boolean(exportConfig.zahrnutPorovnavac) ||
    Boolean(exportConfig.zahrnutPoznamku);

  if (maDalsieSekcie) {
    novaStrana(pdf, stav);
  }

  if (exportConfig.zahrnutPredcasneSplatenie) {
    zapisPredcasneSplatenie(pdf, stav, dataPrePdf);
  }

  if (exportConfig.zahrnutInvestovanie) {
    zapisInvestovanie(pdf, stav, dataPrePdf);
  }

  if (exportConfig.zahrnutPoznamku) {
    stav.y += 5;
    zapisPoznamku(pdf, stav, dataPrePdf);
  }

  if (exportConfig.zahrnutSplatkovyKalendar) {
    const kalendarData = vytvorZlucenyKalendar(dataPrePdf);
    if (Array.isArray(kalendarData?.riadky) && kalendarData.riadky.length) {
      stav.y += 5;
      zapisNadpisSekcie(pdf, stav, 'Splátkový kalendár');
      zapisTabulkuKalendar(pdf, stav, kalendarData);
    }
  }

  zapisDisclaimer(pdf, stav);

  if (exportConfig.zahrnutPorovnavac && dataPrePdf?.porovnavac?.maData) {
    zapisPorovnavac(pdf, stav, dataPrePdf);
  }

  const pocetStran = pdf.getNumberOfPages();
  stav.pocetStran = pocetStran;

  for (let i = 1; i <= pocetStran; i += 1) {
    pdf.setPage(i);
    pridajHlavuAPatu(pdf, i, stav.kontaktPdf, pocetStran);
  }

  const nazovSuboru = bezpecnyNazovSuboru(dataPrePdf?.metadata?.nazovSuboru || 'Ponuka_hypoteka');
  pdf.save(nazovSuboru);
}